// src/providers/dashboard/JobBrowserContext.tsx
import { createContext, useContext } from 'react';
import { useStore } from 'zustand';
import type {
  JobBrowserStore,
  JobBrowserStoreApi,
} from '@/stores/jobBrowserStore';

const JobBrowserStoreContext = createContext<JobBrowserStoreApi | null>(null);

export const JobBrowserStoreProvider = JobBrowserStoreContext.Provider;

/** The raw store instance, for imperative reads (e.g. getState() in event handlers). */
export const useJobBrowserStoreApi = () => {
  const api = useContext(JobBrowserStoreContext);

  if (!api) {
    throw new Error(
      'useJobBrowserStoreApi must be used within DashboardProvider',
    );
  }

  return api;
};

/** Subscribe to a slice of the store with a selector. */
export const useJobBrowserStore = <T,>(
  selector: (state: JobBrowserStore) => T,
) => useStore(useJobBrowserStoreApi(), selector);

export const useJobFilters = () => ({
  filters: useJobBrowserStore((state) => state.filters),
  setFilters: useJobBrowserStore((state) => state.setFilters),
});

export const useJobSelection = () => ({
  select: useJobBrowserStore((state) => state.select),
  selectedId: useJobBrowserStore((state) => state.selectedId),
});

export const useFilterRail = () => ({
  railOpen: useJobBrowserStore((state) => state.railOpen),
  toggleRail: useJobBrowserStore((state) => state.toggleRail),
});

export const useVisibleJobs = () => ({
  setVisibleJobIds: useJobBrowserStore((state) => state.setVisibleJobIds),
  visibleJobIds: useJobBrowserStore((state) => state.visibleJobIds),
});

/** Boolean: is THIS job the selected one. Re-renders only when the boolean flips. */
export const useIsJobSelected = (jobId: number) =>
  useJobBrowserStore((state) => state.selectedId === jobId);

/** Boolean: is anything selected. Re-renders only across the null boundary. */
export const useHasSelection = () =>
  useJobBrowserStore((state) => state.selectedId != null);

/** The stable `select` action alone (no selectedId subscription). */
export const useSelectJob = () => useJobBrowserStore((state) => state.select);
